import { Form } from "antd";
import { useEffect, useState } from "react";
import { modalTitle } from "../controls/InModal.default";
import InModal from "../controls/InModal";
import ModalFooter from "../controls/ModalFooter";
import ModalDeleteConfirmation from "../controls/ModalDeleteConfirmation";
import ModalErrorAlert from "../controls/ModalErrorAlert";
import DynamicForm from "../controls/DynamicForm";
import { DynamicSchema } from "../controls/DynamicForm.default";
import {
  addPlatformAdmin,
  deletePlatformAdmin,
  updatePlatformAdmin,
} from "../repository/data.repository";
import { getProblemMessage } from "../utils/http";
import { phoneRule } from "../controls/PhoneInput";
import { setFormFields } from "../utils/setFormFields";
import { hasFormTouched } from "../utils/hasFormChanged";
import { AdminForm } from "./AdminFormDialog.default";

export interface AdminFormDialogProps {
  readonly open?: boolean;
  readonly mode: "add" | "update" | "delete";
  readonly initialValues?: AdminForm;
  readonly onFinish?: () => void;
  readonly onCancel?: () => void;
}

const formSchema = (mode: "add" | "update" | "delete"): DynamicSchema => ({
  groups: [
    {
      controls: [
        {
          type: "textbox",
          name: "identifier",
          label: "User Name",
          rules: [{ required: true, message: "User name is required" }],
        },
        {
          type: "textbox",
          name: "email",
          label: "Email",
          rules: [
            { required: true, message: "Email is required" },
            { type: "email", message: "Invalid email format" },
          ],
        },
        {
          type: "textbox",
          name: "firstName",
          label: "First Name",
          rules: [{ required: true, message: "First name is required" }],
        },
        {
          type: "textbox",
          name: "middleName",
          label: "Middle Name",
        },
        {
          type: "textbox",
          name: "lastName",
          label: "Last Name",
          rules: [{ required: true, message: "Last name is required" }],
        },
        {
          type: "phonebox",
          name: "phoneNumber",
          label: "Phone Number",
          rules: [phoneRule],
        },
      ],
    },
  ],
});

function AdminFormDialog(props: AdminFormDialogProps) {
  const [form] = Form.useForm();
  const values = Form.useWatch<AdminForm>([], form);
  const [valid, setValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  useEffect(() => {
    if (props.open) {
      if (props.initialValues) setFormFields(form, props.initialValues);
      else form.resetFields();
    } else {
      reset();
    }
  }, [props.open]);

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then((value) => setValid(!!Object.values(value).length))
      .catch(() => setValid(false));
  }, [form, values, props.open]);

  function reset(): void {
    setValid(false);
    setErrorMessage("");
    setLoading(false);
    form.resetFields();
  }

  function onCancel(): void {
    props.onCancel?.();
  }

  function onSubmit(): void {
    setLoading(true);
    setErrorMessage("");

    const payload = {
      identifier: values.identifier!,
      firstName: values.firstName,
      middleName: values.middleName,
      lastName: values.lastName,
      email: values.email,
      phoneNumber: values.phoneNumber,
    };

    if (props.mode === "add") {
      addPlatformAdmin(payload)
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch((err) => {
          setLoading(false);
          setErrorMessage(getProblemMessage(err));
        });
    } else if (props.mode === "update") {
      if (!hasFormTouched(form)) {
        setLoading(false);
        props.onFinish?.();
        return;
      }
      updatePlatformAdmin(props.initialValues?.uid!, payload)
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch((err) => {
          setLoading(false);
          setErrorMessage(getProblemMessage(err));
        });
    } else if (props.mode === "delete") {
      deletePlatformAdmin(props.initialValues?.uid!)
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch((err) => {
          setLoading(false);
          setErrorMessage(getProblemMessage(err));
        });
    }
  }

  return (
    <InModal
      open={props.open}
      size={props.mode === "delete" ? "small" : "medium"}
      title={modalTitle(props.mode, "Admin", props.initialValues?.identifier)}
      onCancel={onCancel}
      actions={
        <ModalFooter
          mode={props.mode}
          loading={loading}
          valid={valid}
          onCancel={onCancel}
          onSubmit={onSubmit}
        />
      }
    >
      {props.mode !== "delete" && (
        <DynamicForm
          className="mb-4 w-auto"
          form={form}
          schema={formSchema(props.mode)}
        />
      )}
      {props.mode === "delete" && (
        <ModalDeleteConfirmation
          entityLabel="admin"
          entityName={props.initialValues?.identifier}
        />
      )}
      <ModalErrorAlert message={errorMessage} />
    </InModal>
  );
}

export default AdminFormDialog;
